/**
 * Schema Builder — Centralized schema generation for all page types
 * ═══════════════════════════════════════════════════════════════════════════
 * SINGLE SOURCE OF TRUTH for all Schema.org structured data generation
 * 
 * ✅ All schema builder functions live here
 * ✅ Import business data from CORE_BUSINESS in coreSchemas.ts
 * ✅ Pages import schema builders from this file only
 * ✅ No duplicate schema functions across multiple files
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 * SCHEMA ID NAMESPACING CONVENTION
 * ═══════════════════════════════════════════════════════════════════════════
 * To prevent ID collisions between static (index.html) and runtime (page) schemas:
 * 
 * ✅ STATIC SCHEMAS (index.html):
 *    - Use prefix: schema-static-[type]
 *    - Examples: schema-static-organization, schema-static-website
 *    - These are site-wide, never change, persist across all pages
 * 
 * ✅ RUNTIME SCHEMAS (pages):
 *    - Use prefix: schema-[pagename]-[type]
 *    - Examples: schema-financing-webpage, schema-inventory-breadcrumb
 *    - These are page-specific, injected/removed on navigation
 * 
 * ✅ USAGE:
 *    injectJsonLd('schema-financing-webpage', buildWebPageSchema({...}));
 *    injectJsonLd('schema-financing-breadcrumb', buildBreadcrumbSchema([...]));
 * 
 * ⚠️ NEVER use schema IDs that conflict with static schemas in index.html
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { CORE_BUSINESS, SCHEMA_IDS, SERVICE_AREAS, OPENING_HOURS, CORE_SERVICES } from './coreSchemas';

// ─── Shared DOM Helpers ───────────────────────────────────────────────────────
/**
 * Inject or update a JSON-LD schema script tag in the document head
 * @param id - Unique identifier for the script tag (use schema-[pagename]-[type] convention)
 * @param data - Schema data object or array of schema objects
 */
export function injectJsonLd(id: string, data: Record<string, unknown> | Record<string, unknown>[] | null): void {
  if (!data) return; // Skip null schemas (e.g., when reviewCount = 0)
  
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Remove a JSON-LD schema script tag from the document head
 * @param id - Unique identifier for the script tag to remove
 */
export function removeJsonLd(id: string): void {
  const el = document.getElementById(id);
  if (el) el.remove();
}

// ─── Breadcrumb Schema ────────────────────────────────────────────────────────
/**
 * Build BreadcrumbList schema
 * @param crumbs - Array of breadcrumb items with name and URL
 */
export function buildBreadcrumbSchema(crumbs: Array<{ name: string; item: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${CORE_BUSINESS.url}/#breadcrumb`,
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: c.item,
    })),
  };
}

// ─── WebPage Schema ───────────────────────────────────────────────────────────
/**
 * Build cross-referenced WebPage schema that connects to core business entities
 * @param config - Page configuration with name, description, URL, breadcrumbs, etc.
 */
export function buildWebPageSchema(config: {
  name: string;
  description: string;
  url: string;
  pageType?: string;
  breadcrumbs?: Array<{ name: string; item: string }>;
  mainEntity?: string; // @id reference to main entity
  keywords?: string;
}) {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': config.pageType || 'WebPage',
    '@id': `${config.url}#webpage`,
    name: config.name,
    description: config.description,
    url: config.url,
    isPartOf: {
      '@id': SCHEMA_IDS.website,
    },
    about: {
      '@id': SCHEMA_IDS.localBusiness,
    },
    publisher: {
      '@id': SCHEMA_IDS.organization,
    },
    copyrightYear: new Date().getFullYear(),
    copyrightHolder: {
      '@id': SCHEMA_IDS.organization,
    },
    inLanguage: 'en-US',
    dateModified: new Date().toISOString().split('T')[0],
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.speakable-headline', '.speakable-summary', '.speakable-content'],
    },
    contactPoint: {
      '@id': SCHEMA_IDS.contactPoint,
    },
  };

  if (config.keywords) {
    schema.keywords = config.keywords;
  }

  if (config.mainEntity) {
    schema.mainEntity = { '@id': config.mainEntity };
  }

  if (config.breadcrumbs) {
    schema.breadcrumb = buildBreadcrumbSchema(config.breadcrumbs);
  }

  return schema;
}

// ─── Speakable Schema ─────────────────────────────────────────────────────────
/**
 * @deprecated Use buildSpeakableSchema from '../../lib/aeoSchemaBuilder' instead.
 * That version is the canonical implementation used by SchemaManager.
 * This stub is kept only to avoid breaking any direct imports during migration.
 */
export function buildSpeakableSchema(cssSelectors: string[], url: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${url}#speakable`,
    url,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: cssSelectors,
    },
  };
}

// ─── LocalBusiness Schema ─────────────────────────────────────────────────────
/**
 * Build unified LocalBusiness schema with automotive specialization
 * ✅ CRITICAL: Only includes aggregateRating when real reviews exist (reviewCount > 0)
 * @param businessType - Type of business (LocalBusiness, AutomotiveBusiness, AutoDealer)
 * @param aggregateRating - Optional aggregate rating data (only included if reviewCount > 0)
 */
export function buildLocalBusinessSchema(
  businessType: 'LocalBusiness' | 'AutomotiveBusiness' | 'AutoDealer' = 'LocalBusiness',
  aggregateRating?: { ratingValue: string | number; reviewCount: number; bestRating?: string; worstRating?: string }
): Record<string, unknown> {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': [businessType, 'LocalBusiness'],
    '@id': businessType === 'AutoDealer' ? SCHEMA_IDS.autoDealer : 
           businessType === 'AutomotiveBusiness' ? SCHEMA_IDS.automotiveBusiness : 
           SCHEMA_IDS.localBusiness,
    name: CORE_BUSINESS.name,
    legalName: CORE_BUSINESS.legalName,
    alternateName: CORE_BUSINESS.alternateName,
    url: CORE_BUSINESS.url,
    telephone: CORE_BUSINESS.telephone,
    email: CORE_BUSINESS.email,
    foundingDate: CORE_BUSINESS.foundingDate,
    description: CORE_BUSINESS.description,
    slogan: CORE_BUSINESS.slogan,
    priceRange: CORE_BUSINESS.priceRange,
    currenciesAccepted: CORE_BUSINESS.currenciesAccepted,
    paymentAccepted: CORE_BUSINESS.paymentAccepted.join(', '),
    logo: {
      '@type': 'ImageObject',
      url: CORE_BUSINESS.logo,
      width: 400,
      height: 120,
    },
    image: [
      CORE_BUSINESS.logo,
      'https://public.readdy.ai/ai/img_res/b7dfa9a758cb668312300fb44407e737.jpg',
    ],
    address: {
      '@type': 'PostalAddress',
      ...CORE_BUSINESS.address,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: CORE_BUSINESS.geo.latitude,
      longitude: CORE_BUSINESS.geo.longitude,
    },
    hasMap: `https://www.google.com/maps/place/Rockhound+Auto/@${CORE_BUSINESS.geo.latitude},${CORE_BUSINESS.geo.longitude},17z`,
    openingHoursSpecification: OPENING_HOURS,
    areaServed: SERVICE_AREAS.map((area) => ({
      '@type': 'City',
      name: area.name,
      containedInPlace: {
        '@type': 'State',
        name: area.state,
      },
    })),
    serviceArea: {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@type': 'GeoCoordinates',
        latitude: CORE_BUSINESS.geo.latitude,
        longitude: CORE_BUSINESS.geo.longitude,
      },
      geoRadius: '300000', // 300km radius
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        '@id': SCHEMA_IDS.contactPoint,
        telephone: CORE_BUSINESS.telephone,
        contactType: 'sales',
        areaServed: ['OR', 'WA', 'US'],
        availableLanguage: 'English',
        hoursAvailable: OPENING_HOURS,
      },
      {
        '@type': 'ContactPoint',
        telephone: CORE_BUSINESS.telephone,
        contactType: 'customer service',
        email: CORE_BUSINESS.email,
        areaServed: ['OR', 'WA', 'US'],
        availableLanguage: 'English',
      },
    ],
    makesOffer: CORE_SERVICES.map((service) => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: service.name,
        description: service.description,
      },
    })),
    sameAs: CORE_BUSINESS.sameAs,
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Rockhound Auto Services',
      itemListElement: CORE_SERVICES.map((service) => ({
        '@type': 'OfferCatalog',
        name: service.name,
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: service.name,
              description: service.description,
            },
          },
        ],
      })),
    },
    amenityFeature: [
      { '@type': 'LocationFeatureSpecification', name: 'Free Vehicle Delivery', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'No-Pressure Sales', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Transparent Pricing', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Vehicle Inspections', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Financing Available', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Trade-In Accepted', value: true },
    ],
    potentialAction: [
      {
        '@type': 'ReserveAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${CORE_BUSINESS.url}/contact`,
          actionPlatform: [
            'http://schema.org/DesktopWebPlatform',
            'http://schema.org/MobileWebPlatform',
          ],
        },
        result: {
          '@type': 'Reservation',
          name: 'Schedule Test Drive',
        },
      },
    ],
  };

  // ✅ CRITICAL: Only add aggregateRating if real reviews exist (reviewCount > 0)
  if (aggregateRating && aggregateRating.reviewCount > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: String(aggregateRating.ratingValue),
      reviewCount: aggregateRating.reviewCount,
      bestRating: aggregateRating.bestRating || '5',
      worstRating: aggregateRating.worstRating || '1',
    };
  }

  return schema;
}

// ─── FAQPage Schema ───────────────────────────────────────────────────────────
/**
 * Build FAQPage schema
 * @param faqs - Array of FAQ items with question and answer
 */
export function buildFAQPageSchema(faqs: Array<{ question: string; answer: string }> | undefined | null) {
  if (!faqs || faqs.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${CORE_BUSINESS.url}/faq#faqpage`,
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer.replace(/<[^>]*>/g, ''),
      },
    })),
  };
}

// ─── AggregateRating Schema ───────────────────────────────────────────────────
/**
 * Build standalone AggregateRating schema
 * ✅ CRITICAL: Only use when reviewCount > 0
 * @param config - Rating configuration with entity type, name, URL, and rating data
 */
export function buildAggregateRatingSchema(config: {
  entityType: string; // e.g., 'FinancialService', 'LocalBusiness', 'Product'
  entityId: string; // e.g., 'https://rockhoundauto.com/#financial-service'
  name: string;
  url: string;
  ratingValue: string | number;
  reviewCount: number;
  bestRating?: string | number;
  worstRating?: string | number;
}) {
  if (config.reviewCount === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': config.entityType,
    '@id': config.entityId,
    name: config.name,
    url: config.url,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: String(config.ratingValue),
      reviewCount: config.reviewCount,
      bestRating: String(config.bestRating || 5),
      worstRating: String(config.worstRating || 1),
    },
  };
}

// ─── Article Schema ───────────────────────────────────────────────────────────
/**
 * Build Article schema for blog posts
 * @param article - Article data with title, excerpt, image, date, author, etc.
 */
export function buildArticleSchema(article: {
  title: string;
  excerpt: string;
  heroImage: string;
  date: string;
  author: string;
  slug: string;
  readTime?: string;
  category?: string;
  tags?: string[];
  updatedAt?: string;
}) {
  const articleUrl = `${CORE_BUSINESS.url}/blog/${article.slug}`;
  const publishDate = new Date(article.date).toISOString();
  const modifiedDate = article.updatedAt
    ? new Date(article.updatedAt).toISOString()
    : publishDate;

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': articleUrl,
    headline: article.title,
    description: article.excerpt,
    image: {
      '@type': 'ImageObject',
      url: article.heroImage,
      width: 1200,
      height: 600,
    },
    datePublished: publishDate,
    dateModified: modifiedDate,
    author: {
      '@type': 'Person',
      name: article.author,
      url: CORE_BUSINESS.url,
    },
    publisher: {
      '@type': 'Organization',
      name: CORE_BUSINESS.name,
      url: CORE_BUSINESS.url,
      logo: {
        '@type': 'ImageObject',
        url: CORE_BUSINESS.logo,
        width: 400,
        height: 120,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    isPartOf: {
      '@id': SCHEMA_IDS.website,
    },
    ...(article.readTime ? { timeRequired: article.readTime } : {}),
    ...(article.category ? { articleSection: article.category } : {}),
    ...(article.tags && article.tags.length > 0
      ? { keywords: article.tags.join(', ') }
      : {}),
    inLanguage: 'en-US',
    isAccessibleForFree: true,
  };
}

// ─── Vehicle Product Schema ───────────────────────────────────────────────────
/**
 * Build Product schema for a vehicle
 * @param vehicle - Vehicle data with year, make, model, price, mileage, etc.
 */
export function buildVehicleProductSchema(vehicle: {
  id: string;
  year: number;
  make: string;
  model: string;
  price: number;
  mileage: number;
  vin?: string;
  status: string;
  transmission?: string;
  drivetrain?: string;
  exteriorColor?: string;
  bodyType?: string;
  description?: string;
  image?: string;
}) {
  const vehicleUrl = `${CORE_BUSINESS.url}/inventory/${vehicle.id}`;
  const vehicleName = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  const validFrom = new Date().toISOString().split('T')[0];
  const priceValidUntil = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0];

  const availability =
    vehicle.status === 'available'
      ? 'https://schema.org/InStock'
      : vehicle.status === 'pending'
      ? 'https://schema.org/LimitedAvailability'
      : 'https://schema.org/SoldOut';

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${vehicleUrl}#product`,
    name: vehicleName,
    description:
      vehicle.description ||
      `${vehicleName} with ${vehicle.mileage.toLocaleString()} miles. ${
        vehicle.transmission || ''
      } transmission, ${vehicle.drivetrain || ''} drivetrain. Available at Rockhound Auto in Warrenton, Oregon.`,
    ...(vehicle.image ? { image: vehicle.image } : {}),
    brand: {
      '@type': 'Brand',
      name: vehicle.make,
    },
    ...(vehicle.vin
      ? {
          sku: vehicle.vin,
          mpn: vehicle.vin,
          identifier: [
            { '@type': 'PropertyValue', propertyID: 'VIN', value: vehicle.vin },
          ],
        }
      : {}),
    itemCondition: 'https://schema.org/UsedCondition',
    url: vehicleUrl,
    offers: {
      '@type': 'Offer',
      '@id': `${vehicleUrl}#offer`,
      url: vehicleUrl,
      priceCurrency: 'USD',
      price: vehicle.price,
      validFrom,
      priceValidUntil,
      itemCondition: 'https://schema.org/UsedCondition',
      availability,
      seller: {
        '@type': 'AutomotiveBusiness',
        '@id': SCHEMA_IDS.organization,
      },
    },
  };
}

// ─── Inventory ItemList Schema ────────────────────────────────────────────────
/**
 * Build ItemList schema for vehicle inventory
 * @param vehicles - Array of vehicle data
 */
export function buildInventoryListSchema(
  vehicles: Array<{
    id: string;
    year: number;
    make: string;
    model: string;
    price: number;
    mileage: number;
    vin?: string;
    status: string;
    bodyType?: string;
  }>
) {
  if (!vehicles.length) return null;

  const validFrom = new Date().toISOString().split('T')[0];
  const priceValidUntil = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0];

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Rockhound Auto Vehicle Inventory — Used Cars Warrenton Oregon',
    description:
      'Browse quality pre-owned vehicles at Rockhound Auto in Warrenton, Oregon. Serving Astoria, Seaside, Tillamook, and the Pacific Northwest. Transparent pricing, no hidden fees.',
    url: `${CORE_BUSINESS.url}/inventory`,
    numberOfItems: vehicles.length,
    itemListElement: vehicles.slice(0, 20).map((v, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${CORE_BUSINESS.url}/inventory/${v.id}`,
      name: `${v.year} ${v.make} ${v.model}`,
      item: {
        '@type': 'Car',
        name: `${v.year} ${v.make} ${v.model}`,
        brand: { '@type': 'Brand', name: v.make },
        model: v.model,
        vehicleModelDate: String(v.year),
        ...(v.vin
          ? {
              vehicleIdentificationNumber: v.vin,
              identifier: {
                '@type': 'PropertyValue',
                propertyID: 'VIN',
                value: v.vin,
              },
            }
          : {}),
        mileageFromOdometer: {
          '@type': 'QuantitativeValue',
          value: v.mileage,
          unitCode: 'SMI',
        },
        ...(v.bodyType ? { bodyType: v.bodyType } : {}),
        url: `${CORE_BUSINESS.url}/inventory/${v.id}`,
        itemCondition: 'https://schema.org/UsedCondition',
        offers: {
          '@type': 'Offer',
          price: v.price,
          priceCurrency: 'USD',
          validFrom,
          priceValidUntil,
          itemCondition: 'https://schema.org/UsedCondition',
          availability:
            v.status === 'available'
              ? 'https://schema.org/InStock'
              : 'https://schema.org/SoldOut',
          seller: {
            '@type': 'AutoDealer',
            '@id': SCHEMA_IDS.organization,
          },
        },
      },
    })),
  };
}

// ─── AggregateOffer Schema ────────────────────────────────────────────────────
/**
 * Build AggregateOffer schema for inventory pricing
 * @param vehicles - Array of vehicles with price and status
 */
export function buildAggregateOfferSchema(
  vehicles: Array<{ price: number; status: string }>
) {
  const available = vehicles.filter((v) => v.status === 'available');
  if (!available.length) return null;

  const prices = available.map((v) => v.price);
  const lowPrice = Math.min(...prices);
  const highPrice = Math.max(...prices);
  const offerCount = available.length;

  const validFrom = new Date().toISOString().split('T')[0];
  const priceValidUntil = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0];

  return {
    '@context': 'https://schema.org',
    '@type': 'AggregateOffer',
    '@id': `${CORE_BUSINESS.url}/inventory#aggregate-offer`,
    name: 'Used Car Inventory — Rockhound Auto, Warrenton Oregon',
    description: `${offerCount} quality pre-owned vehicles available now at Rockhound Auto in Warrenton, Oregon. Prices from $${lowPrice.toLocaleString()} to $${highPrice.toLocaleString()}.`,
    url: `${CORE_BUSINESS.url}/inventory`,
    priceCurrency: 'USD',
    lowPrice,
    highPrice,
    offerCount,
    validFrom,
    priceValidUntil,
    itemCondition: 'https://schema.org/UsedCondition',
    availability: 'https://schema.org/InStock',
    seller: {
      '@type': 'AutoDealer',
      '@id': SCHEMA_IDS.organization,
      name: CORE_BUSINESS.name,
      url: CORE_BUSINESS.url,
      telephone: CORE_BUSINESS.telephone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: CORE_BUSINESS.address.streetAddress,
        addressLocality: CORE_BUSINESS.address.addressLocality,
        addressRegion: CORE_BUSINESS.address.addressRegion,
        postalCode: CORE_BUSINESS.address.postalCode,
        addressCountry: CORE_BUSINESS.address.addressCountry,
      },
    },
  };
}

// ─── ContactPage Schema ───────────────────────────────────────────────────────
/**
 * Build ContactPage schema
 */
export function buildContactPageSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    '@id': `${CORE_BUSINESS.url}/contact#contactpage`,
    name: 'Contact Rockhound Auto',
    description:
      'Contact Rockhound Auto in Warrenton, Oregon. Visit us, call, or send a message. Serving Astoria, Seaside, Tillamook, and the Pacific Northwest.',
    url: `${CORE_BUSINESS.url}/contact`,
    isPartOf: {
      '@id': SCHEMA_IDS.website,
    },
    mainEntity: {
      '@id': SCHEMA_IDS.localBusiness,
    },
    publisher: {
      '@id': SCHEMA_IDS.organization,
    },
  };
}

// ─── FinancialService Schema ──────────────────────────────────────────────────
/**
 * Build FinancialService schema for financing pages
 * @param config - Optional configuration for custom name, description, URL
 */
export function buildFinancialServiceSchema(config?: {
  name?: string;
  description?: string;
  url?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FinancialService',
    '@id': `${CORE_BUSINESS.url}/financing#financialservice`,
    name: config?.name || 'Rockhound Auto Financing',
    description:
      config?.description ||
      'Auto loan financing for all credit situations — bad credit, no credit, first-time buyers welcome. We shop your loan to multiple lenders to find the best rate.',
    url: config?.url || `${CORE_BUSINESS.url}/financing`,
    telephone: CORE_BUSINESS.telephone,
    provider: {
      '@id': SCHEMA_IDS.organization,
    },
    areaServed: [
      { '@type': 'State', name: 'Oregon' },
      { '@type': 'State', name: 'Washington' },
    ],
    serviceType: 'Auto Loan Financing',
  };
}

// ─── VideoObject Schema ───────────────────────────────────────────────────────
/**
 * Convert duration string (MM:SS or M:SS) to ISO 8601 duration (PT#M#S)
 * @param duration - Duration string like "12:34" or "1:05"
 * @returns ISO 8601 duration string like "PT12M34S"
 */
function convertDurationToISO8601(duration: string): string {
  const parts = duration.split(':');
  if (parts.length === 2) {
    const minutes = parseInt(parts[0], 10);
    const seconds = parseInt(parts[1], 10);
    return `PT${minutes}M${seconds}S`;
  }
  return 'PT0S';
}

/**
 * Build VideoObject schema for a single video
 * @param video - Video item with platform, videoId, title, description, etc.
 * @returns Schema.org VideoObject
 */
export function buildVideoObjectSchema(video: {
  id: string;
  title: string;
  description: string;
  platform: 'youtube' | 'tiktok';
  videoId: string;
  thumbnail: string;
  publishedDate: string;
  duration: string;
  /** Raw view count as a number */
  views?: number;
  year?: number;
  make?: string;
  model?: string;
}) {
  const isYouTube = video.platform === 'youtube';
  const contentUrl = isYouTube
    ? `https://www.youtube.com/watch?v=${video.videoId}`
    : `https://www.tiktok.com/@rockhoundauto/video/${video.videoId}`;

  const embedUrl = isYouTube
    ? `https://www.youtube.com/embed/${video.videoId}`
    : undefined;

  const uploadDate = new Date(video.publishedDate).toISOString();
  const isoDuration = convertDurationToISO8601(video.duration);

  // Parse view count for interactionStatistic
  let watchCount = 0;
  if (video.views) {
    watchCount = Math.round(video.views);
  }

  // Generate keywords from title, make, model
  const keywords: string[] = ['used cars', 'Rockhound Auto', 'Warrenton Oregon'];
  if (video.make) keywords.push(video.make);
  if (video.model) keywords.push(video.model);
  if (video.year) keywords.push(String(video.year));
  keywords.push('vehicle walkthrough', 'car video tour');

  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    '@id': `${CORE_BUSINESS.url}/videos#video-${video.id}`,
    name: video.title,
    description: video.description,
    thumbnailUrl: video.thumbnail,
    uploadDate,
    duration: isoDuration,
    contentUrl,
    ...(embedUrl ? { embedUrl } : {}),
    publisher: {
      '@type': 'Organization',
      '@id': SCHEMA_IDS.organization,
      name: CORE_BUSINESS.name,
      url: CORE_BUSINESS.url,
      logo: {
        '@type': 'ImageObject',
        url: CORE_BUSINESS.logo,
        width: 400,
        height: 120,
      },
    },
    inLanguage: 'en-US',
    regionsAllowed: 'US',
    isFamilyFriendly: true,
    ...(watchCount > 0
      ? {
          interactionStatistic: {
            '@type': 'InteractionCounter',
            interactionType: 'https://schema.org/WatchAction',
            userInteractionCount: watchCount,
          },
        }
      : {}),
    keywords: keywords.join(', '),
    about: {
      '@id': SCHEMA_IDS.localBusiness,
    },
  };
}

/**
 * Build ItemList of VideoObject schemas for multiple videos
 * @param videos - Array of video items
 * @returns Schema.org ItemList containing VideoObject items
 */
export function buildVideoListSchema(
  videos: Array<{
    id: string;
    title: string;
    description: string;
    platform: 'youtube' | 'tiktok';
    videoId: string;
    thumbnail: string;
    publishedDate: string;
    duration: string;
  }>
) {
  if (!videos.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${CORE_BUSINESS.url}/videos#video-list`,
    name: 'Rockhound Auto Vehicle Videos',
    description:
      'Watch detailed video tours, test drives, and walkarounds of our used cars, trucks, and SUVs.',
    url: `${CORE_BUSINESS.url}/videos`,
    numberOfItems: videos.length,
    itemListElement: videos.map((video, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: buildVideoObjectSchema(video),
    })),
  };
}

// ─── Pre-Built Page Schema Sets ──────────────────────────────────────────────
/**
 * PAGE_SCHEMAS — Pre-built schema sets for common page types
 * Use these to inject multiple related schemas at once
 */
export const PAGE_SCHEMAS = {
  /**
   * Financing page schema set
   * @param aggregateRating - Optional rating data from Supabase reviews
   */
  financing: (aggregateRating?: { ratingValue: number; reviewCount: number }) => ({
    webpage: buildWebPageSchema({
      name: `Car Financing Oregon Coast | Auto Loans for All Credit | ${CORE_BUSINESS.name}`,
      description:
        'Get approved for auto financing in minutes. All credit situations welcome. Competitive rates, fast approval, no hidden fees.',
      url: `${CORE_BUSINESS.url}/financing`,
      breadcrumbs: [
        { name: 'Home', item: CORE_BUSINESS.url },
        { name: 'Financing', item: `${CORE_BUSINESS.url}/financing` },
      ],
    }),
    breadcrumb: buildBreadcrumbSchema([
      { name: 'Home', item: CORE_BUSINESS.url },
      { name: 'Financing', item: `${CORE_BUSINESS.url}/financing` },
    ]),
    financialService: buildFinancialServiceSchema(),
    localBusiness: buildLocalBusinessSchema('AutomotiveBusiness', aggregateRating),
  }),

  /**
   * Trade-in page schema set
   */
  tradeIn: () => ({
    webpage: buildWebPageSchema({
      name: `Trade In Your Car | Fair Market Value | ${CORE_BUSINESS.name}`,
      description:
        'Get a fair trade-in value for your vehicle. Transparent appraisals, competitive offers, instant quotes.',
      url: `${CORE_BUSINESS.url}/trade-in`,
      breadcrumbs: [
        { name: 'Home', item: CORE_BUSINESS.url },
        { name: 'Trade-In', item: `${CORE_BUSINESS.url}/trade-in` },
      ],
    }),
    breadcrumb: buildBreadcrumbSchema([
      { name: 'Home', item: CORE_BUSINESS.url },
      { name: 'Trade-In', item: `${CORE_BUSINESS.url}/trade-in` },
    ]),
    localBusiness: buildLocalBusinessSchema('AutoDealer'),
  }),

  /**
   * Contact page schema set
   */
  contact: () => ({
    webpage: buildWebPageSchema({
      name: `Contact ${CORE_BUSINESS.name} | Warrenton Oregon`,
      description: `Contact ${CORE_BUSINESS.name} in Warrenton, Oregon. Visit us, call, or send a message.`,
      url: `${CORE_BUSINESS.url}/contact`,
      breadcrumbs: [
        { name: 'Home', item: CORE_BUSINESS.url },
        { name: 'Contact', item: `${CORE_BUSINESS.url}/contact` },
      ],
    }),
    breadcrumb: buildBreadcrumbSchema([
      { name: 'Home', item: CORE_BUSINESS.url },
      { name: 'Contact', item: `${CORE_BUSINESS.url}/contact` },
    ]),
    contactPage: buildContactPageSchema(),
    localBusiness: buildLocalBusinessSchema('LocalBusiness'),
  }),
};